import { Button, HSpacer, InfoTooltip, TextLink } from '@/components/DesignSystem';
import { ChurnStatusChip } from '@/components/shared/ChurnStatusChip';
import { CustomerChurn } from '@/constants/constant';
import { LinkedAccountsDialog } from '@/pages/FarmerList/LinkedAccountsDialog';
import {
  ViewCustomerDetailsFullScreenModal,
} from '@/pages/RetailerOverview/ViewCustomerDetailsFullScreenModal';
import { getProgramReward } from '@/utilities/ChurnCustomer';
import { RetailerEndpoint } from '@api/endpoints';
import { ApiUser } from '@api/interfaces';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { ChurnStatus } from '@shared/enums';
import { formatNumber, getAccountName, getTierName } from '@shared/utilities';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ExtendedApiUser extends ApiUser {
  outstandingPointsIssued?: number,
}

interface ChurnTableBodyProps {
  customers?: ExtendedApiUser[],
  program: ApiRewardsProgram,
}

export const ChurnTableData: React.FC<ChurnTableBodyProps> = ({ customers, program }) => {

  const { t, i18n } = useTranslation();
  const [selectedLinkedAccountsCustomer, setSelectedLinkedAccountsCustomer] = useState<ApiUser>();
  const [selectedViewAccountCustomer, setSelectedViewAccountCustomer] = useState<ApiUser>();

  return (
    <>
      <TableBody>
        {!!customers && customers.map((customer: ExtendedApiUser) => (
          <TableRow key={customer.id}>
            <TableCell height={67} sx={{ overflowWrap: 'anywhere' }}>
              {getAccountName(customer)}
            </TableCell>
            <TableCell height={67} sx={{ overflowWrap: 'anywhere' }}>
              <ChurnStatusChip status={customer?.churnStatus as ChurnStatus} />
            </TableCell>
            <TableCell height={67}>
              {getTierName(
                program.tiers ?? [],
                getProgramReward(customer.farmerRewards, program.id)?.rankCurrent,
                t($ => $.common.status.none),
              )}
            </TableCell>
            <TableCell height={67}>
              {getTierName(
                program.tiers ?? [],
                getProgramReward(customer.farmerRewards, program.id)?.rankEarned,
                t($ => $.common.status.none),
              )}
            </TableCell>
            <TableCell height={67}>
              {formatNumber(customer?.outstandingPointsIssued ?? 0, i18n)}
            </TableCell>
            <TableCell height={67}>
              {!customer.accountGroupUsers?.length
                ? t($ => $.common.status.none)
                : (
                  <TextLink
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedLinkedAccountsCustomer(customer);
                    }}
                    testID={`${customer.id}-linked-churn-accounts-button`}
                  >
                    {t(
                      $ =>
                        $.common.units.accountsCount,
                      { count: customer.accountGroupUsers?.length ?? 0 },
                    )}
                  </TextLink>
                )
              }
            </TableCell>
            <TableCell height={67}>
              <Button
                onClick={() => setSelectedViewAccountCustomer(customer)}
                size="small"
                square
                testID={`${customer.id}-view-churn-account-button`}
              >
                {t($ => $.common.actions.viewAccount)}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      {!!selectedLinkedAccountsCustomer && (
        <LinkedAccountsDialog
          customer={selectedLinkedAccountsCustomer}
          onClose={() => setSelectedLinkedAccountsCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
          show
        />
      )}
      {!!selectedViewAccountCustomer && (
        <ViewCustomerDetailsFullScreenModal
          id={selectedViewAccountCustomer.id}
          onClose={() => setSelectedViewAccountCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
        />
      )}
    </>
  );
};

interface SortProps {
  sortColumns?: RetailerEndpoint.ListTierCustomers.TierCustomersSort[],
  sortBy?: string,
  sortDesc?: boolean,
  handleSort?: (field: RetailerEndpoint.ListTierCustomers.TierCustomersSort) => void,
}

export const ChurnTableHeader: React.FC<SortProps> = ({
  sortColumns,
  sortBy,
  sortDesc,
  handleSort,
}) => {

  return (
    <>
      <TableHead>
        <TableRow>
          {Object.values(CustomerChurn.columns).map((col) => {
            const isSortable =
              !!sortColumns &&
              sortColumns.includes(
                col.key as RetailerEndpoint.ListTierCustomers.TierCustomersSort,
              );
            const content = (
              <>
                {col.label}
                <HSpacer size="1" />
                {'description' in col && !!col.description && (
                  <InfoTooltip color="info">{col.description}</InfoTooltip>
                )}
              </>
            );
            return (
              <TableCell
                height={72}
                key={col.key}
                variant="head"
                width={col.width}
              >
                {isSortable ? (
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortDesc ? 'desc' : 'asc'}
                    onClick={() =>
                      handleSort?.(
                        col.key as RetailerEndpoint.ListTierCustomers.TierCustomersSort)
                    }
                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    {content}
                  </TableSortLabel>
                ) : (
                  content
                )}
              </TableCell>
            );
          })}
          <TableCell height={72} variant="head" width={135} />
        </TableRow>
      </TableHead>
    </>
  );
};
